import { useStyles } from './DiagnosticSummary.style';

import { Typography, Tooltip, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../../../../store/index';
import {
  densityClassColor,
  diagnosticsClassColor,
  getRiskColor,
  qualityClassColor,
} from '../../../../../helpers/manageColors';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';

const DiagnosticTimeline = ({ data, smallScreen }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const results = useSelector((state) => state.results);
  const quality_values = useSelector((state) => state.global_settings.quality_system.quality_values);
  const globalSettings = useSelector((store) => store.global_settings ?? {});
  const { language } = globalSettings || {};

  const currentStudy = results.exam_id;

  // Transform new data format to old format
  const transformData = (rawData) => {
    return rawData?.map((item) => {
      // Determine highest BI-RADS opacities
      const opacities = item.biradsOpacities ? item.biradsOpacities : null;

      // Determine highest BI-RADS calcific
      const microcalcification = item.biradsCalc ? item.biradsCalc : null;

      // Convert quality array to single quality value (take the worst quality found)
      let quality = null;
      if (item.quality && Array.isArray(item.quality) && quality_values && quality_values.length > 0) {
        const nonNullQualities = item.quality.filter((q) => q !== null);
        if (nonNullQualities.length > 0) {
          // Find the worst quality by finding the one with the highest index in quality_values
          let worstQualityIndex = -1;
          let worstQuality = nonNullQualities[0];

          nonNullQualities.forEach((q) => {
            const index = quality_values.indexOf(q);
            if (index > worstQualityIndex) {
              worstQualityIndex = index;
              worstQuality = q;
            }
          });

          quality = worstQuality;
        }
      }

      return {
        microcalcification,
        opacities,
        quality,
        overall_density: item.dens_max || null,
        risk: item.risk,
        study_id: item.StudyInstanceUID,
        study_date: item.date,
        has_new_lesions: item.has_new_lesions,
        new_lesion_class: item.new_lesion_class,
        new_lesion_projection: item.new_lesion_projection,
        not_operated: item.not_operated,
        surgery: item.surgery,
      };
    });
  };

  const transformedLeftData = transformData(data?.historyLeft) || [];
  const transformedRightData = transformData(data?.historyRight) || [];

  const leftDataWithSide = transformedLeftData.map((item) => ({ ...item, side: 'left' }));
  const rightDataWithSide = transformedRightData.map((item) => ({ ...item, side: 'right' }));

  const hasValidDate = (date) => date && !Number.isNaN(new Date(date).getTime());

  const getDateKey = (date) => (hasValidDate(date) ? new Date(date).toISOString() : null);

  const timelineData = (Array.isArray(data) ? data : [...rightDataWithSide, ...leftDataWithSide]).filter(
    (item) => item?.side && hasValidDate(item?.study_date),
  );

  const allStudyDates = Array.from(
    new Set(timelineData.map((item) => getDateKey(item.study_date)).filter(Boolean)),
  ).sort((a, b) => new Date(a) - new Date(b));

  const hasPreviousStudies = allStudyDates.length > 1;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language || undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getWorstSeverity = (microcalcification, opacities) => {
    const severities = ['birads1', 'vessel', 'birads2', 'birads3', 'birads4', 'birads5', 'lesionKnown'];

    const validSeverities = [];

    if (microcalcification && microcalcification !== 'no_microcalc') {
      validSeverities.push(microcalcification);
    }
    if (opacities && opacities !== 'no_opacities') {
      validSeverities.push(opacities);
    }

    if (validSeverities.length === 0) {
      return microcalcification === 'no_microcalc' ? 'birads1' : 'birads1';
    }

    return validSeverities.reduce((worst, current) => {
      return severities.indexOf(current) > severities.indexOf(worst) ? current : worst;
    });
  };

  const timePointColor = (microcalcification, opacities) => {
    if (!microcalcification && !opacities) {
      return theme.palette.grey[700];
    }
    const worstSeverity = getWorstSeverity(microcalcification, opacities);
    if (
      (microcalcification || opacities) &&
      !(microcalcification === 'no_microcalc' && opacities === 'no_opacities')
    ) {
      return `${diagnosticsClassColor(worstSeverity, theme)}`;
    }

    return `${diagnosticsClassColor('birads1', theme)}`;
  };

  const getBiradsNumber = (microcalcification, opacities) => {
    if (!microcalcification && !opacities) {
      return '-';
    }
    const severity = getWorstSeverity(microcalcification, opacities);
    const match = severity?.match(/^birads(\d)$/i);
    return match ? match[1] : severity || '-';
  };

  const renderTimelineTooltipContent = (item) => {
    return (
      <div className={classes.timelineContent}>
        {item.study_id === currentStudy && (
          <>
            <Typography className={classes.timelineCurrent}>
              {t('ResultView.current_results_shown')}
            </Typography>
          </>
        )}
        <Typography className={classes.timelineDate}>
          {t(`diagnose.${item.side}_breast`)} - {formatDate(item.study_date)}
        </Typography>

        <>
          {!item.microcalcification && !item.opacities && !item.overall_density && !item.quality ? (
            <Typography className={classes.timelineLabel}>{t('Dashboard.not_evaluated')}</Typography>
          ) : (
            <div className={classes.timelineDetails}>
              <div className={classes.timelineDetail}>
                <Typography className={classes.timelineLabel}>{t('report.postop')}:</Typography>
                <Typography
                  style={{
                    color:
                      item?.surgery || !item?.not_operated
                        ? theme.palette.qualityClasses.postop
                        : theme.palette.grey[400],
                  }}
                  className={classes.timelineValue}>
                  {item?.surgery || !item?.not_operated
                    ? t('Filters.postop_operated')
                    : t('ResultView.is_not_operated')}
                </Typography>
              </div>

              <div className={classes.timelineDetail}>
                <Typography className={classes.timelineLabel}>{t('report.risk')}:</Typography>
                <Typography
                  className={classes.timelineValue}
                  style={{ color: getRiskColor(item.risk, theme) }}>
                  {item.risk ? item.risk : t('risk_management.no_risk')}
                </Typography>
              </div>
              <div className={classes.timelineDetail}>
                <Typography className={classes.timelineLabel}>{t('ResultView.density')}:</Typography>
                <Typography
                  className={classes.timelineValue}
                  style={{
                    color: densityClassColor(item.density || item.overall_density, theme),
                  }}>
                  {item.density || item.overall_density}
                </Typography>
              </div>
              <div className={classes.timelineDetail}>
                <Typography className={classes.timelineLabel}>{t('ResultView.quality')}:</Typography>
                <Typography
                  className={classes.timelineValue}
                  style={{
                    color:
                      item.quality === 'N/A'
                        ? theme.palette.status.notEvaluated
                        : qualityClassColor(item.quality, theme),
                  }}>
                  {t(`Quality.${item.quality}`)?.toUpperCase()}
                </Typography>
              </div>
              <div className={classes.timelineDetail}>
                <Typography className={classes.timelineLabel}>
                  {t('projections.microcalcifications')}:
                </Typography>
                <Typography
                  className={classes.timelineValue}
                  style={{
                    color: diagnosticsClassColor(item.microcalcification, theme),
                  }}>
                  {t(`ResultView.${item.microcalcification}`)}
                </Typography>
              </div>
              <div className={classes.timelineDetail}>
                <Typography className={classes.timelineLabel}>{t('projections.opacity')}:</Typography>
                <Typography
                  className={classes.timelineValue}
                  style={{
                    color: diagnosticsClassColor(item.opacities, theme),
                  }}>
                  {t(`ResultView.${item.opacities}`)}
                </Typography>
              </div>
            </div>
          )}
        </>
      </div>
    );
  };

  const getStudyYear = (dateString) => new Date(dateString).getUTCFullYear();

  const getShortDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${day}.${month}`;
  };

  const getDaysInYear = (year) => {
    const startOfYear = Date.UTC(year, 0, 0);
    const endOfYear = Date.UTC(year + 1, 0, 0);

    return Math.floor((endOfYear - startOfYear) / 86400000);
  };

  const getDayOfYear = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const startOfYear = Date.UTC(year, 0, 0);
    const currentDay = Date.UTC(year, date.getUTCMonth(), date.getUTCDate());

    return Math.floor((currentDay - startOfYear) / 86400000);
  };

  const getPositionInYear = (dateString) => {
    const year = getStudyYear(dateString);
    const position = (getDayOfYear(dateString) / getDaysInYear(year)) * 100;

    return Math.min(Math.max(position, 0), 100);
  };

  const sideOrder = {
    right: 0,
    left: 1,
  };

  const sortTimelineData = (items = []) =>
    [...items].sort((a, b) => {
      const dateDiff = new Date(a.study_date) - new Date(b.study_date);

      if (dateDiff !== 0) {
        return dateDiff;
      }

      return (sideOrder[a.side] ?? 2) - (sideOrder[b.side] ?? 2);
    });

  const TIMELINE_POINT_INSET = 32;
  const OVERLAP_CLUSTER_DISTANCE = 8;
  const OVERLAP_SPACING = 28;

  const getPointLeftPosition = (position, horizontalOffset) => {
    const insetCorrection = ((TIMELINE_POINT_INSET * 2 * position) / 100).toFixed(2);
    const offsetOperator = horizontalOffset < 0 ? '-' : '+';
    const offsetValue = Math.abs(horizontalOffset);

    return `calc(${TIMELINE_POINT_INSET}px + ${position}% - ${insetCorrection}px ${offsetOperator} ${offsetValue}px)`;
  };

  const getOverlapOffset = (items, index) => {
    const position = getPositionInYear(items[index].study_date);
    const cluster = items
      .map((item, itemIndex) => ({
        itemIndex,
        position: getPositionInYear(item.study_date),
      }))
      .filter((item) => Math.abs(item.position - position) < OVERLAP_CLUSTER_DISTANCE);

    if (cluster.length <= 1) {
      return 0;
    }

    const clusterIndex = cluster.findIndex((item) => item.itemIndex === index);

    if (position < 12) {
      return clusterIndex * OVERLAP_SPACING;
    }

    if (position > 88) {
      return -(cluster.length - 1 - clusterIndex) * OVERLAP_SPACING;
    }

    return clusterIndex * OVERLAP_SPACING - ((cluster.length - 1) * OVERLAP_SPACING) / 2;
  };

  const renderPointIndicators = (item) => (
    <>
      {item?.surgery && (
        <Tooltip
          arrow
          title={<Typography className={classes.tooltip_texts}>{t('Filters.postop_operated')}</Typography>}>
          <LocalHospitalIcon
            className={classes.postopIndicator}
            style={{
              fill: `${theme.palette.qualityClasses.postop}`,
            }}
          />
        </Tooltip>
      )}
      {item?.study_id && !item?.not_operated && !item?.surgery && (
        <Tooltip
          arrow
          title={<Typography className={classes.tooltip_texts}>{t('report.postop')}</Typography>}>
          <div
            className={classes.operated_indicator}
            style={{
              background: theme.palette.qualityClasses.postop,
            }}
          />
        </Tooltip>
      )}
      {(item?.has_new_lesions || item?.new_lesion_class) && (
        <>
          {item?.new_lesion_projection && (
            <Typography className={classes.newLesionIndicatorText}>{item?.new_lesion_projection}</Typography>
          )}

          <Tooltip
            arrow
            title={
              <Typography className={classes.tooltip_texts}>
                {t('diagnose.newLesionIndicator')} {t(`ResultView.${item?.new_lesion_class}`)}
              </Typography>
            }>
            <div
              className={classes.newLesionIndicator}
              style={{
                background: item?.new_lesion_class
                  ? diagnosticsClassColor(item?.new_lesion_class, theme)
                  : '#c79853',
              }}
            />
          </Tooltip>
        </>
      )}
    </>
  );

  const renderTimelinePoint = (item, sideItems, index, side, isRightBreast) => {
    const position = getPositionInYear(item.study_date);
    const horizontalOffset = getOverlapOffset(sideItems, index);
    const isCurrentStudy = item.study_id === currentStudy;

    return (
      <div
        key={`${item.study_id || item.study_date}-${side}-${index}`}
        className={classes.timelineYearPoint}
        style={{
          left: getPointLeftPosition(position, horizontalOffset),
          bottom: side === 'right' ? '60%' : undefined,
          top: side === 'left' ? '60%' : undefined,
        }}
        onClick={() => {
          if (item.study_id) {
            dispatch(actions.showExamination(item.study_id));
          }
        }}>
        {isCurrentStudy && !isRightBreast && <div className={classes.currentStudyIndicator} />}

        {side === 'right' && (
          <div
            className={classes.timelinePointConnectorBottom}
            style={{ backgroundColor: isCurrentStudy ? '#298a8a' : '#595959' }}
          />
        )}

        {side === 'right' && (
          <Typography
            className={classes.timelinePointDateTop}
            style={{
              fontWeight: isCurrentStudy ? 'bold' : undefined,
              color: isCurrentStudy ? '#298a8a' : theme.palette.text.secondary,
            }}>
            {getShortDate(item.study_date)}
          </Typography>
        )}

        <div className={classes.timelineMarkerShell}>
          <Tooltip arrow placement='bottom-end' title={renderTimelineTooltipContent(item)}>
            <div
              className={classes.timelineCircle}
              style={{
                border: isCurrentStudy ? `2px solid ${theme.palette.accents.light}` : `1px solid transparent`,
                width: isCurrentStudy ? '18px' : '16px',
                height: isCurrentStudy ? '18px' : '16px',
                backgroundColor: timePointColor(item.microcalcification, item.opacities),
                position: 'relative',
                boxShadow: isCurrentStudy
                  ? `rgb(245 245 245) 0px 0px 5px 0px, rgb(21 214 225) 0px 0px 5px 0px`
                  : 'none',
              }}>
              <Typography
                className={classes.timelineCircleInner}
                style={{
                  backgroundColor: timePointColor(item.microcalcification, item.opacities),
                }}>
                {getBiradsNumber(item?.microcalcification, item?.opacities)}
              </Typography>
            </div>
          </Tooltip>
          {renderPointIndicators(item)}
        </div>
        {side === 'left' && (
          <div
            className={classes.timelinePointConnectorTop}
            style={{ backgroundColor: isCurrentStudy ? '#298a8a' : '#595959' }}
          />
        )}
      </div>
    );
  };

  // Render a single horizontal timeline split into right/left rows by year.
  const renderTimeline = (items) => {
    const sortedData = sortTimelineData(items);

    const timelineYears = Array.from(new Set(sortedData.map((item) => getStudyYear(item.study_date)))).sort(
      (a, b) => a - b,
    );

    const hasData = sortedData.length > 0;

    const getYearMinWidth = (rightItemsLength, leftItemsLength) => {
      const maxItemsInRow = Math.max(rightItemsLength, leftItemsLength, 1);
      return Math.max(140, maxItemsInRow * 52);
    };

    const getYearColumnStyle = (rightItemsLength, leftItemsLength) => {
      const minWidth = getYearMinWidth(rightItemsLength, leftItemsLength);

      return {
        flex: `1 0 ${minWidth}px`,
        minWidth: `${minWidth}px`,
      };
    };

    return (
      <div
        className={classes.timelineWrapper}
        style={{
          padding: hasPreviousStudies ? '0 16px' : '28px 16px',
        }}>
        {hasData ? (
          <div className={classes.timelineScrollArea}>
            <div className={classes.timelineLabelsColumn}>
              <Typography className={classes.timelineSideTexts}>{t('ResultView.right')}</Typography>
              <Typography className={classes.timelineSideTexts}>{t('ResultView.left')}</Typography>
            </div>
            <div className={classes.timelineYears}>
              <div className={classes.timelineMiddleLine} />

              {timelineYears.map((year) => {
                const rightItems = sortedData.filter(
                  (item) => item.side === 'right' && getStudyYear(item.study_date) === year,
                );
                const leftItems = sortedData.filter(
                  (item) => item.side === 'left' && getStudyYear(item.study_date) === year,
                );

                return (
                  <div
                    key={year}
                    className={classes.timelineYearColumn}
                    style={getYearColumnStyle(rightItems.length, leftItems.length)}>
                    <div className={classes.timelineYearEdge} />
                    <div className={classes.timelineYearRow}>
                      {rightItems.map((item, index) =>
                        renderTimelinePoint(item, rightItems, index, 'right', true),
                      )}
                    </div>
                    <div className={classes.timelineYearRow}>
                      {leftItems.map((item, index) =>
                        renderTimelinePoint(item, leftItems, index, 'left', false),
                      )}
                    </div>
                    <Typography className={classes.timelineYearLabel}>{year}</Typography>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            <Typography className={classes.timelineEmpty}>{t('diagnose.no_prior_study')}</Typography>
            <div className={classes.timelineLineWrapperEmpty}>
              <div className={classes.timelineLineEmpty} />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={classes.diagnostic_sections_container} style={{ height: '45%' }}>
      <Typography className={classes.diagnostic_table_title}>{t('diagnose.trend')}</Typography>

      {/* Single timeline with left/right indicators or empty timeline */}
      {hasPreviousStudies ? renderTimeline(timelineData) : renderTimeline([])}
    </div>
  );
};

export default DiagnosticTimeline;
